/**
 * Mock profile-administration service.
 *
 * In-memory ProfileService for the prototype. Generates a display-only phrase and
 * PIN at creation time; these are cosmetic stand-ins, NOT the entropy-checked
 * phrase generation or the HMAC/Argon2id handling that Phase 3 introduces.
 */

import { makeDemoProfiles } from "./fixtures.js";
import { hasObviousPinPattern } from "../../ui/pin-strength.js";

/** Demo word pool for the cosmetic phrase. Not the real word list (§8). */
const DEMO_WORDS = [
  "harbour",
  "willow",
  "lantern",
  "meadow",
  "compass",
  "thistle",
  "beacon",
  "cinder",
];

/**
 * The mock's contact allowance.
 *
 * Mirrors the free tier in `functions/src/shared/config.js`. The real value comes
 * from the server with each profile; the mock supplies its own so the prototype
 * and the e2e suite exercise the same "you have used all your contacts" path.
 */
const MOCK_MAX_CONTACTS = 3;

let nextId = 1;

/**
 * @param {string} prefix
 * @returns {string}
 */
function makeId(prefix) {
  return `${prefix}_${Date.now().toString(36)}_${nextId++}`;
}

/**
 * The number of words in a generated phrase.
 *
 * **Must match `PHRASE_WORD_COUNT` in `functions/src/security/wordlist.js`.** The
 * mock is cosmetic, but its *shape* is not: the prototype, the e2e suite and the
 * on-screen hint all describe what a real phrase looks like, and a mock that
 * disagrees teaches everyone the wrong thing. This was three for a while after
 * the real generator moved to four, and the mismatch only surfaced because the
 * deployed site was accidentally running on mocks.
 */
const MOCK_PHRASE_WORDS = 4;

/** A cosmetic phrase. Real generation, entropy and the curated list are §8. */
function makeMockPhrase() {
  const pick = () => DEMO_WORDS[Math.floor(Math.random() * DEMO_WORDS.length)];
  return Array.from({ length: MOCK_PHRASE_WORDS }, pick).join("-");
}

/** A cosmetic 6-digit PIN. Real generation is Phase 3. */
function makeMockPin() {
  return String(Math.floor(100000 + Math.random() * 900000));
}

/**
 * @returns {import("../interfaces.js").ProfileService}
 */
export function createMockProfileService() {
  /** @type {import("../../types/models.js").RecoveryProfile[]} */
  const profiles = makeDemoProfiles();

  return {
    async list() {
      return profiles.map((profile) => ({ ...profile, maxContacts: MOCK_MAX_CONTACTS }));
    },

    async get(id) {
      const found = profiles.find((profile) => profile.id === id);
      return found ? { ...found, maxContacts: MOCK_MAX_CONTACTS } : null;
    },

    async create(label) {
      /** @type {import("../../types/models.js").RecoveryProfile} */
      const profile = {
        id: makeId("prof"),
        label,
        enabled: true,
        contacts: [],
        phrase: makeMockPhrase(),
        pin: makeMockPin(),
      };
      profiles.push(profile);
      return { ...profile };
    },

    async rename(profileId, label) {
      const profile = profiles.find((candidate) => candidate.id === profileId);
      if (!profile) {
        throw new Error("That profile could not be found.");
      }
      profile.label = label;
    },

    async addContact(profileId, contact) {
      const profile = profiles.find((candidate) => candidate.id === profileId);
      if (!profile) {
        throw new Error("That profile could not be found.");
      }
      // The mock enforces the limit too, so the prototype and the e2e suite see
      // the same rejection the server would give.
      if (profile.contacts.length >= MOCK_MAX_CONTACTS) {
        throw new Error(`Your plan allows up to ${MOCK_MAX_CONTACTS} contacts in a profile.`);
      }
      /** @type {import("../../types/models.js").Contact} */
      const created = { ...contact, id: makeId("con") };
      profile.contacts.push(created);
      return { ...created };
    },

    async updateContact(profileId, contact) {
      const profile = profiles.find((candidate) => candidate.id === profileId);
      const index = profile?.contacts.findIndex((candidate) => candidate.id === contact.id);
      if (!profile || index === undefined || index < 0) {
        throw new Error("That contact could not be found.");
      }
      profile.contacts[index] = { ...contact };
    },

    async removeContact(profileId, contactId) {
      const profile = profiles.find((candidate) => candidate.id === profileId);
      if (!profile) {
        throw new Error("That profile could not be found.");
      }
      profile.contacts = profile.contacts.filter((candidate) => candidate.id !== contactId);
    },

    async setEnabled(profileId, enabled) {
      const profile = profiles.find((candidate) => candidate.id === profileId);
      if (!profile) {
        throw new Error("That profile could not be found.");
      }
      // Contacts are deliberately untouched — disabling is reversible (§12).
      profile.enabled = enabled;
    },

    async remove(profileId) {
      const index = profiles.findIndex((candidate) => candidate.id === profileId);
      if (index < 0) {
        throw new Error("That profile could not be found.");
      }
      profiles.splice(index, 1);
    },

    async regenerateCredentials(profileId, options) {
      const profile = profiles.find((candidate) => candidate.id === profileId);
      if (!profile) {
        throw new Error("That profile could not be found.");
      }
      /** @type {{ phrase?: string, pin?: string }} */
      const issued = {};
      if (options?.phrase) {
        profile.phrase = makeMockPhrase();
        issued.phrase = profile.phrase;
      }
      if (options?.pin ?? true) {
        if (options?.customPin) {
          if (hasObviousPinPattern(options.customPin)) {
            throw new Error(
              "That PIN is too easy to guess. Avoid repeating or sequential numbers.",
            );
          }
          issued.pin = options.customPin;
        } else {
          issued.pin = makeMockPin();
        }
      }
      return issued;
    },
  };
}
